import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    ClipboardList,
    Plus,
    Search,
    Clock,
    Dumbbell,
    Users,
    MoreVertical,
    Edit,
    Trash2,
    Copy,
    PlayCircle,
    Loader2,
    Calendar,
    Zap,
} from 'lucide-react';

interface Workout {
    id: string;
    name: string;
    description: string | null;
    duration_minutes: number | null;
    difficulty: string;
    focus_areas: string[];
    is_template: boolean;
    is_active: boolean;
    created_at: string;
    _count?: {
        blocks: number;
        prescriptions: number;
    };
}

export function WorkoutsPage() {
    const { tenant } = useAuth();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTemplate, setFilterTemplate] = useState<boolean | null>(null);

    // Fetch workouts with counts
    const { data: workouts, isLoading } = useQuery({
        queryKey: ['workouts', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return [];

            const { data, error } = await supabase
                .from('workouts')
                .select(`
                    *,
                    workout_blocks(count),
                    workout_prescriptions(count)
                `)
                .eq('tenant_id', tenant.id)
                .eq('is_active', true)
                .order('created_at', { ascending: false });

            if (error) throw error;

            return (data || []).map((w) => ({
                ...w,
                _count: {
                    blocks: (w.workout_blocks as { count: number }[])?.[0]?.count || 0,
                    prescriptions: (w.workout_prescriptions as { count: number }[])?.[0]?.count || 0,
                },
            })) as Workout[];
        },
        enabled: !!tenant?.id,
    });

    // Duplicate workout
    const duplicateMutation = useMutation({
        mutationFn: async (workoutId: string) => {
            // First get the workout with blocks and exercises
            const { data: original, error: fetchError } = await supabase
                .from('workouts')
                .select(`
                    *,
                    workout_blocks (
                        *,
                        block_exercises (*)
                    )
                `)
                .eq('id', workoutId)
                .single();

            if (fetchError) throw fetchError;

            // Create new workout
            const { data: newWorkout, error: createError } = await supabase
                .from('workouts')
                .insert({
                    tenant_id: original.tenant_id,
                    name: `${original.name} (Cópia)`,
                    description: original.description,
                    duration_minutes: original.duration_minutes,
                    difficulty: original.difficulty,
                    focus_areas: original.focus_areas,
                    is_template: original.is_template,
                    is_active: true,
                })
                .select()
                .single();

            if (createError) throw createError;

            // Duplicate blocks and exercises
            for (const block of original.workout_blocks || []) {
                const { data: newBlock, error: blockError } = await supabase
                    .from('workout_blocks')
                    .insert({
                        workout_id: newWorkout.id,
                        name: block.name,
                        block_type: block.block_type,
                        order_index: block.order_index,
                        notes: block.notes,
                    })
                    .select()
                    .single();

                if (blockError) throw blockError;

                // Duplicate exercises in block
                for (const exercise of block.block_exercises || []) {
                    await supabase.from('block_exercises').insert({
                        block_id: newBlock.id,
                        exercise_id: exercise.exercise_id,
                        order_index: exercise.order_index,
                        sets: exercise.sets,
                        reps: exercise.reps,
                        weight_kg: exercise.weight_kg,
                        rest_seconds: exercise.rest_seconds,
                        tempo: exercise.tempo,
                        notes: exercise.notes,
                    });
                }
            }

            return newWorkout;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workouts'] });
        },
    });

    // Delete workout
    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('workouts')
                .update({ is_active: false })
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workouts'] });
        },
    });

    // Filter workouts
    const filteredWorkouts = workouts?.filter((w) => {
        const matchesSearch =
            w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            w.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter =
            filterTemplate === null || w.is_template === filterTemplate;
        return matchesSearch && matchesFilter;
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <ClipboardList className="w-6 h-6" />
                        Meus Treinos
                    </h1>
                    <p className="section-subtitle">
                        {workouts?.length} treinos criados
                    </p>
                </div>
                <Link to="/workouts/new" className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Treino
                </Link>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar treinos..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input pl-10"
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilterTemplate(null)}
                        className={`btn ${filterTemplate === null ? 'btn-primary' : 'btn-secondary'}`}
                    >
                        Todos
                    </button>
                    <button
                        onClick={() => setFilterTemplate(true)}
                        className={`btn ${filterTemplate === true ? 'btn-primary' : 'btn-secondary'}`}
                    >
                        Templates
                    </button>
                    <button
                        onClick={() => setFilterTemplate(false)}
                        className={`btn ${filterTemplate === false ? 'btn-primary' : 'btn-secondary'}`}
                    >
                        Personalizados
                    </button>
                </div>
            </div>

            {/* Workouts Grid */}
            {filteredWorkouts && filteredWorkouts.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredWorkouts.map((workout) => (
                        <WorkoutCard
                            key={workout.id}
                            workout={workout}
                            onDuplicate={() => duplicateMutation.mutate(workout.id)}
                            onDelete={() => {
                                if (confirm('Tem certeza que deseja excluir este treino?')) {
                                    deleteMutation.mutate(workout.id);
                                }
                            }}
                        />
                    ))}
                </div>
            ) : (
                <div className="card text-center py-12">
                    <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 mb-2">Nenhum treino ainda</h3>
                    <p className="text-gray-500 mb-4">Crie seu primeiro treino para prescrever aos alunos</p>
                    <Link to="/workouts/new" className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Criar Primeiro Treino
                    </Link>
                </div>
            )}
        </div>
    );
}

// Workout Card Component
function WorkoutCard({
    workout,
    onDuplicate,
    onDelete,
}: {
    workout: Workout;
    onDuplicate: () => void;
    onDelete: () => void;
}) {
    const [showMenu, setShowMenu] = useState(false);

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'Iniciante':
                return 'badge-success';
            case 'Intermediário':
                return 'badge-warning';
            case 'Avançado':
                return 'badge-danger';
            default:
                return 'badge-secondary';
        }
    };

    return (
        <div className="card-hover">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{workout.name}</h3>
                        {workout.is_template && (
                            <span className="badge badge-primary text-xs">Template</span>
                        )}
                    </div>
                    {workout.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                            {workout.description}
                        </p>
                    )}
                </div>

                {/* Menu */}
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>

                    {showMenu && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 z-20 min-w-[160px]">
                                <Link
                                    to={`/workouts/${workout.id}`}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Edit className="w-4 h-4" />
                                    Editar
                                </Link>
                                <button
                                    onClick={() => {
                                        onDuplicate();
                                        setShowMenu(false);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Copy className="w-4 h-4" />
                                    Duplicar
                                </button>
                                <Link
                                    to={`/workouts/${workout.id}/prescribe`}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Users className="w-4 h-4" />
                                    Prescrever
                                </Link>
                                <button
                                    onClick={() => {
                                        onDelete();
                                        setShowMenu(false);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Excluir
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-4">
                {workout.duration_minutes && (
                    <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{workout.duration_minutes} min</span>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <Dumbbell className="w-4 h-4" />
                    <span>{workout._count?.blocks || 0} blocos</span>
                </div>
                <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{workout._count?.prescriptions || 0} alunos</span>
                </div>
            </div>

            {/* Focus areas */}
            {workout.focus_areas && workout.focus_areas.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                    {workout.focus_areas.map((area) => (
                        <span key={area} className="badge badge-secondary text-xs">
                            {area}
                        </span>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t">
                <span className={`badge ${getDifficultyColor(workout.difficulty)}`}>
                    {workout.difficulty}
                </span>
                <div className="flex gap-2">
                    <Link
                        to={`/workouts/${workout.id}`}
                        className="btn-ghost btn-sm"
                    >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                    </Link>
                    <Link
                        to={`/workouts/${workout.id}/prescribe`}
                        className="btn-primary btn-sm"
                    >
                        <PlayCircle className="w-4 h-4 mr-1" />
                        Prescrever
                    </Link>
                </div>
            </div>
        </div>
    );
}
